import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { moduleApi } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  ArrowLeft, Plus, MoreVertical, Pencil, Trash2, Search,
  FileText, Settings, AlertTriangle, Lock, Mail, MailCheck,
} from "lucide-react";
import { formatEuro, formatDate, mapReservationFromApi, reservationToApiPayload, type Board, type Reservation } from "./types";
import { NewReservationModal } from "./NewReservationModal";
import { BoardSettingsModal } from "./BoardSettingsModal";
import { SendAlertModal } from "./SendAlertModal";
import { AlertPreviewModal } from "./AlertPreviewModal";
import { exportPdf } from "./exportUtils";

const HEADERS = [
  "NOM", "PRÉNOM", "TÉL.", "HEURE", "DATE CRÉA.", "DATE PAIEM.",
  "AD.", "ENF.", "CVT.", "T.AD.", "T.ENF.", "M.AD.", "M.ENF.",
  "PRIX TOTAL", "CB", "AMEX", "ESPÈCE", "CHÈQUE", "ZEN", "VIRM",
  "RESTE", "ENCAISSÉ", "PRISE PAR", "ENCAISSÉ PAR", "COMMENTAIRE", "TABLE", "",
];

const SORTABLE_KEYS: (string | null)[] = [
  "lastName", "firstName", "phone", "time", "createdDate", "paidDate",
  "adults", "children", "covers", "tarifAdult", "tarifChild", "montantAd", "montantEnf",
  "prixTotal", "cb", "amex", "cash", "cheque", "zen", "virm",
  "reste", "totalEncaisse", "takenBy", "cashedBy", "comment", "tableNumber", null,
];

export function BoardDetail({
  onBack,
  board,
  takenByOptions,
  cashedByOptions,
  onUpdateSettings,
}: {
  onBack: () => void;
  board: Board | undefined;
  takenByOptions: string[];
  cashedByOptions: string[];
  onUpdateSettings: (settings: { title: string; takenByOptions: string[]; cashedByOptions: string[] }) => void;
}) {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isSendAlertModalOpen, setIsSendAlertModalOpen] = useState(false);
  const [previewAlertReservationId, setPreviewAlertReservationId] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const isArchived = !!board?.isArchived;

  const loadReservations = async () => {
    if (!board?.id) return;
    try {
      const data = await moduleApi.getExceptionServiceBoardReservations(board.id);
      if (Array.isArray(data)) setReservations(data.map((r: any) => mapReservationFromApi(r, board.title)));
    } catch (e) {
      console.error("Failed to load reservations", e);
    }
  };

  useEffect(() => {
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board?.id, board?.title]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredReservations = normalizedSearch
    ? reservations.filter((r) => `${r.lastName} ${r.firstName} ${r.phone} ${r.comment}`.toLowerCase().includes(normalizedSearch))
    : reservations;

  const sortedReservations = useMemo(() => {
    if (!sortKey) return filteredReservations;
    const copy = [...filteredReservations];
    const getValue = (r: Reservation): number | string => {
      switch (sortKey) {
        case "lastName": return r.lastName || "";
        case "firstName": return r.firstName || "";
        case "phone": return r.phone || "";
        case "time": return r.time || "";
        case "createdDate": return r.createdDate || "";
        case "paidDate": return r.paidDate || "";
        case "adults": return r.adults;
        case "children": return r.children;
        case "covers": return r.adults + r.children;
        case "tarifAdult": return r.tarifAdult;
        case "tarifChild": return r.tarifChild;
        case "montantAd": return r.adults * r.tarifAdult;
        case "montantEnf": return r.children * r.tarifChild;
        case "prixTotal": return r.adults * r.tarifAdult + r.children * r.tarifChild;
        case "cb": return r.cb;
        case "amex": return r.amex;
        case "cash": return r.cash;
        case "cheque": return r.cheque;
        case "zen": return r.zen;
        case "virm": return r.virm;
        case "reste": {
          const total = r.adults * r.tarifAdult + r.children * r.tarifChild;
          const paid = r.cb + r.amex + r.cash + r.cheque + r.zen + r.virm;
          return total - paid;
        }
        case "takenBy": return r.takenBy || "";
        case "cashedBy": return r.cashedBy || "";
        case "comment": return r.comment || "";
        case "tableNumber": return r.tableNumber || "";
        default: return "";
      }
    };
    copy.sort((a, b) => {
      const va = getValue(a), vb = getValue(b);
      if (typeof va === "number" && typeof vb === "number") return sortDirection === "asc" ? va - vb : vb - va;
      const sa = String(va).toLowerCase(), sb = String(vb).toLowerCase();
      if (sa < sb) return sortDirection === "asc" ? -1 : 1;
      if (sa > sb) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredReservations, sortKey, sortDirection]);

  const totalsSource = sortedReservations;
  const totalAdults = totalsSource.reduce((sum, r) => sum + (Number(r.adults) || 0), 0);
  const totalChildren = totalsSource.reduce((sum, r) => sum + (Number(r.children) || 0), 0);
  const totalCovers = totalAdults + totalChildren;
  const totalAdultsAmount = totalsSource.reduce((sum, r) => sum + (Number(r.adults) || 0) * (Number(r.tarifAdult) || 0), 0);
  const totalChildrenAmount = totalsSource.reduce((sum, r) => sum + (Number(r.children) || 0) * (Number(r.tarifChild) || 0), 0);
  const totalTheoretical = totalAdultsAmount + totalChildrenAmount;
  const totalCb = totalsSource.reduce((sum, r) => sum + (Number(r.cb) || 0), 0);
  const totalAmex = totalsSource.reduce((sum, r) => sum + (Number(r.amex) || 0), 0);
  const totalCash = totalsSource.reduce((sum, r) => sum + (Number(r.cash) || 0), 0);
  const totalCheque = totalsSource.reduce((sum, r) => sum + (Number(r.cheque) || 0), 0);
  const totalZen = totalsSource.reduce((sum, r) => sum + (Number(r.zen) || 0), 0);
  const totalVirm = totalsSource.reduce((sum, r) => sum + (Number(r.virm) || 0), 0);
  const totalEncaisse = totalCb + totalAmex + totalCash + totalCheque + totalZen + totalVirm;
  const totalResteAPayer = totalTheoretical - totalEncaisse;

  const coversBySize: { [key: number]: number } = {};
  sortedReservations.forEach((r) => {
    const covers = r.adults + r.children;
    coversBySize[covers] = (coversBySize[covers] || 0) + 1;
  });

  const stats = [
    { label: "Total couverts", value: String(totalCovers), color: "text-content" },
    { label: "Total encaissements", value: formatEuro(totalEncaisse), color: "text-emerald-500" },
    { label: "Total CA théorique", value: formatEuro(totalTheoretical), color: "text-content" },
    { label: "Reste à payer", value: formatEuro(totalResteAPayer), color: "text-warning-500" },
  ];

  const handleHeaderClick = (index: number) => {
    const key = SORTABLE_KEYS[index];
    if (!key) return;
    if (sortKey === key) setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDirection("asc"); }
  };

  const handleSaveReservation = async (reservation: Reservation) => {
    if (!board?.id) return;
    try {
      if (editingReservation) {
        await moduleApi.updateExceptionServiceReservation(editingReservation.id, reservationToApiPayload(reservation));
        setReservations((prev) => prev.map((r) => (r.id === editingReservation.id ? { ...reservation, id: editingReservation.id } : r)));
      } else {
        const res: any = await moduleApi.createExceptionServiceReservation(board.id, reservationToApiPayload(reservation));
        setReservations((prev) => [...prev, { ...reservation, id: res.id }]);
      }
    } catch (e) {
      console.error("Failed to save reservation", e);
    }
  };

  const handleDeleteReservation = async (id: number) => {
    if (!confirm("Supprimer cette réservation ?")) return;
    try {
      await moduleApi.deleteExceptionServiceReservation(id);
    } catch (e) {
      console.error("Failed to delete reservation", e);
    }
    setReservations((prev) => prev.filter((x) => x.id !== id));
  };

  const handleDeleteAllReservations = async () => {
    if (!confirm("Supprimer TOUTES les réservations de ce tableau ?")) return;
    try {
      await Promise.all(reservations.map((r) => moduleApi.deleteExceptionServiceReservation(r.id)));
    } catch (e) {
      console.error("Failed to delete all reservations", e);
    }
    setReservations([]);
  };

  return (
    <div className="space-y-6 pb-24">
      <div>
        <button onClick={onBack} className="flex items-center gap-2 text-sm font-bold text-content-muted hover:text-content transition-colors mb-3">
          <ArrowLeft size={16} /> Retour à la liste des tableaux
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-black text-content">{board?.title ?? "Tableau"}</h1>
          {isArchived && (
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-surface-soft border border-border text-content-muted text-xs font-bold">
              <Lock size={12} /> Archivé — lecture seule
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-content-muted" />
          <input
            type="text"
            placeholder="Rechercher une réservation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-surface border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors"
          />
        </div>
        <div className="flex items-center gap-2">
          {!isArchived && (
            <>
              <button onClick={() => setIsSendAlertModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-sm font-bold transition-all">
                <Mail size={16} /> Envoyer une alerte au client
              </button>
              <button onClick={() => { setEditingReservation(null); setIsModalOpen(true); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold shadow-lg shadow-brand-500/20 transition-all">
                <Plus size={16} /> Nouvelle réservation
              </button>
            </>
          )}
          <div className="relative">
            <button onClick={() => setIsDropdownOpen((v) => !v)} className="p-2.5 rounded-xl bg-surface-soft hover:bg-surface border border-border text-content-muted hover:text-content transition-all">
              <MoreVertical size={18} />
            </button>
            <AnimatePresence>
              {isDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 top-[calc(100%+8px)] w-64 bg-surface border border-border rounded-2xl shadow-2xl z-50 p-2 space-y-0.5"
                  >
                    <button onClick={() => { exportPdf(board?.title, filteredReservations, user?.theme_color || undefined); setIsDropdownOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-content hover:bg-surface-soft transition-all">
                      <FileText size={16} className="text-red-400" /> Exporter PDF / Imprimer
                    </button>
                    {!isArchived && (
                      <>
                        <div className="h-px bg-border my-1" />
                        <button onClick={() => { setIsDropdownOpen(false); setIsSettingsModalOpen(true); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-content hover:bg-surface-soft transition-all">
                          <Settings size={16} /> Paramètres du tableau
                        </button>
                        <div className="h-px bg-border my-1" />
                        <button onClick={() => { setIsDropdownOpen(false); handleDeleteAllReservations(); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-400 hover:bg-red-500/10 transition-all">
                          <AlertTriangle size={16} /> Supprimer toutes les réservations
                        </button>
                      </>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-surface border border-border rounded-2xl p-4">
            <div className="text-xs text-content-muted mb-1">{stat.label}</div>
            <div className={`text-xl font-black ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      {Object.keys(coversBySize).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(coversBySize).sort(([a], [b]) => Number(a) - Number(b)).map(([size, count]) => (
            <div key={size} className="bg-brand-500/10 border border-brand-500/20 rounded-xl px-3 py-2 text-center">
              <span className="text-[11px] text-content-muted font-bold uppercase tracking-wide mr-1.5">Table {size}:</span>
              <span className="text-brand-600 dark:text-brand-400 font-black text-sm">{count}</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-surface border border-border rounded-2xl overflow-auto max-h-[50vh]">
        <table className="w-full text-left min-w-[1500px] text-xs">
          <thead className="border-b border-border">
            <tr>
              {HEADERS.map((header, i) => {
                const isSortable = SORTABLE_KEYS[i] != null;
                const isActive = isSortable && sortKey === SORTABLE_KEYS[i];
                const stickyCls = i === 0 ? "sticky left-0 z-30 w-[110px] min-w-[110px]" : i === 1 ? "sticky left-[110px] z-30 w-[110px] min-w-[110px]" : "";
                return (
                  <th
                    key={i}
                    onClick={() => handleHeaderClick(i)}
                    className={`px-2.5 py-3 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap text-content-muted bg-surface-soft sticky top-0 z-20 ${stickyCls} ${isSortable ? "cursor-pointer hover:text-content" : ""} ${isActive ? "text-brand-600 dark:text-brand-400" : ""}`}
                  >
                    {header}
                    {isActive && (sortDirection === "asc" ? " ▲" : " ▼")}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedReservations.map((r) => {
              const montantAd = r.adults * r.tarifAdult;
              const montantEnf = r.children * r.tarifChild;
              const prixTotal = montantAd + montantEnf;
              const totalPaiement = r.cb + r.amex + r.cash + r.cheque + r.zen + r.virm;
              const restePayer = prixTotal - totalPaiement;
              const td = "px-2.5 py-2 text-center text-content";
              return (
                <tr key={r.id} className="group hover:bg-surface-soft/60 transition-colors">
                  <td className={`${td} text-left font-bold sticky left-0 z-10 w-[110px] min-w-[110px] bg-surface group-hover:bg-surface-soft transition-colors truncate`} title={r.lastName}>{r.lastName.toUpperCase()}</td>
                  <td className={`${td} text-left sticky left-[110px] z-10 w-[110px] min-w-[110px] bg-surface group-hover:bg-surface-soft transition-colors truncate`} title={r.firstName}>{r.firstName}</td>
                  <td className={td}>{r.phone}</td>
                  <td className={td}>{r.time}</td>
                  <td className={td}>{formatDate(r.createdDate)}</td>
                  <td className={td}>{formatDate(r.paidDate)}</td>
                  <td className={td}>{r.adults}</td>
                  <td className={td}>{r.children}</td>
                  <td className={td}>{r.adults + r.children}</td>
                  <td className={td}>{r.tarifAdult.toFixed(2)}€</td>
                  <td className={td}>{r.tarifChild.toFixed(2)}€</td>
                  <td className={td}>{montantAd.toFixed(2)}€</td>
                  <td className={td}>{montantEnf.toFixed(2)}€</td>
                  <td className={`${td} font-bold`}>{prixTotal.toFixed(2)}€</td>
                  <td className={td}>{r.cb.toFixed(2)}€</td>
                  <td className={td}>{r.amex.toFixed(2)}€</td>
                  <td className={td}>{r.cash.toFixed(2)}€</td>
                  <td className={td}>{r.cheque.toFixed(2)}€</td>
                  <td className={td}>{r.zen.toFixed(2)}€</td>
                  <td className={td}>{r.virm.toFixed(2)}€</td>
                  <td className={`${td} text-warning-500 font-bold`}>{restePayer.toFixed(2)}€</td>
                  <td className={`${td} text-emerald-500 font-bold`}>{totalPaiement.toFixed(2)}€</td>
                  <td className={td}>{r.takenBy}</td>
                  <td className={td}>{r.cashedBy}</td>
                  <td className={`${td} text-left max-w-[160px] truncate`} title={r.comment}>{r.comment}</td>
                  <td className={`${td} font-bold bg-brand-500/5`}>{r.tableNumber}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1">
                      {r.email && (
                        r.alertSentAt ? (
                          <button onClick={() => setPreviewAlertReservationId(r.id)}
                            title={`Alerte envoyée le ${new Date(r.alertSentAt).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} — cliquer pour voir l'e-mail`}
                            className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition-colors">
                            <MailCheck size={14} />
                          </button>
                        ) : (
                          <span title="N'a pas encore reçu l'alerte groupée" className="p-1.5 rounded-lg text-content-muted opacity-50">
                            <Mail size={14} />
                          </span>
                        )
                      )}
                      {!isArchived && (
                        <>
                          <button onClick={() => { setEditingReservation(r); setIsModalOpen(true); }} className="p-1.5 rounded-lg text-brand-600 dark:text-brand-400 hover:bg-brand-500/10 transition-all">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => handleDeleteReservation(r.id)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-all">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr className="bg-brand-500/5 font-bold text-content">
              <td colSpan={6} className="px-2.5 py-3 text-emerald-500 sticky left-0 z-10 bg-surface">Totaux (filtrés) :</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{totalAdults}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{totalChildren}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{totalCovers}</td>
              <td /><td />
              <td className="px-2.5 py-3 text-center text-[11px]">{formatEuro(totalAdultsAmount)}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{formatEuro(totalChildrenAmount)}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{formatEuro(totalTheoretical)}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{formatEuro(totalCb)}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{formatEuro(totalAmex)}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{formatEuro(totalCash)}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{formatEuro(totalCheque)}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{formatEuro(totalZen)}</td>
              <td className="px-2.5 py-3 text-center text-[11px]">{formatEuro(totalVirm)}</td>
              <td className="px-2.5 py-3 text-center text-[11px] text-warning-500">{formatEuro(totalResteAPayer)}</td>
              <td className="px-2.5 py-3 text-center text-[11px] text-emerald-500">{formatEuro(totalEncaisse)}</td>
              <td /><td /><td /><td />
            </tr>
          </tbody>
        </table>
      </div>

      <div className="fixed bottom-0 left-0 md:left-[var(--sidebar-w,248px)] right-0 bg-brand-600 text-brand-fg px-6 py-3 flex flex-wrap items-center justify-center gap-6 text-xs z-30 shadow-2xl transition-[left] duration-200">
        <span className="opacity-90">
          CB: {formatEuro(totalCb)} • AMEX: {formatEuro(totalAmex)} • ESPÈCE: {formatEuro(totalCash)} • CHÈQUE: {formatEuro(totalCheque)} • ZEN: {formatEuro(totalZen)} • VIRM: {formatEuro(totalVirm)}
        </span>
        <span className="font-black">TOTAL ENCAISSÉ: {formatEuro(totalEncaisse)}</span>
        <span className="font-black bg-red-500/20 border border-red-300/30 px-2.5 py-1 rounded-full">RESTE À PAYER: {formatEuro(totalResteAPayer)}</span>
      </div>

      {!isArchived && (
        <button
          onClick={() => { setEditingReservation(null); setIsModalOpen(true); }}
          className="fixed right-6 bottom-24 w-14 h-14 rounded-full bg-brand-600 hover:bg-brand-700 text-brand-fg shadow-2xl flex items-center justify-center transition-all z-30"
        >
          <Plus size={24} />
        </button>
      )}

      {isModalOpen && (
        <NewReservationModal
          onClose={() => setIsModalOpen(false)}
          initialData={editingReservation}
          onSave={handleSaveReservation}
          takenByOptions={takenByOptions}
          cashedByOptions={cashedByOptions}
        />
      )}
      {isSendAlertModalOpen && board?.id && (
        <SendAlertModal
          boardId={board.id}
          onClose={() => setIsSendAlertModalOpen(false)}
          onSent={loadReservations}
        />
      )}
      {previewAlertReservationId !== null && (
        <AlertPreviewModal reservationId={previewAlertReservationId} onClose={() => setPreviewAlertReservationId(null)} />
      )}
      {isSettingsModalOpen && (
        <BoardSettingsModal
          onClose={() => setIsSettingsModalOpen(false)}
          initialTitle={board?.title ?? ""}
          initialTakenBy={takenByOptions}
          initialCashedBy={cashedByOptions}
          onSave={(settings) => { onUpdateSettings(settings); setIsSettingsModalOpen(false); }}
        />
      )}
    </div>
  );
}
